import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import { ITechnicianScorecard } from '../../../../app/entities/TechnicianScorecard'
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { getElapsedTime } from '../../global/utils/date/dates'
import getAndSetScorecardWeightsAndTargets from '../../global/utils/technician-scorecard/get-weights-and-targets'
import {
  ENTRY,
  SCORECARD_TARGETS,
  SCORECARD_WEIGHTS,
  SECTION,
} from '../../global/constants/technician-scorecard'
import PrimaryButton from '../../global/components/buttons/primary-button/PrimaryButton'

//! keep in sync with /app/utils/technician-scorecard/get/get-scorecard-weights.ts
const initialScorecardWeights = {
  driverSafetyWeight: 0.25,
  qualityScoreWeight: 0.25,
  onTimeClockInPercentageWeight: 0.05,
  averageCustomerRatingWeight: 0.05,
  percentageOfJobsRatedByCustomersWeight: 0.05,
  installationAuditPassPercentageWeight: 0.2,
  averageFourTireInstallTimeWeight: 0.1,
  mileageCapturePercentageWeight: 0.05,
}
const initialScorecardTargets = {
  percentageOfJobsRatedByCustomersTarget: 0.5,
  averageFourTireInstallTimeTarget: 70,
  mileageCapturePercentageTarget: 0.85,
  rawQualityScoreTarget: 100,
  rawQualityScoreCutoffTarget: 500,
}

interface ITechnicianScorecardModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  technicianScorecard: ITechnicianScorecard | null
  setTechnicianScorecard: Dispatch<SetStateAction<ITechnicianScorecard | null>>
}

function formatNumberForDisplay(
  number: number | undefined | null,
  unitString = '',
  decimalPlaces = 1,
): string {
  const isNan = number === null || number === undefined || isNaN(number)
  if (isNan) return 'N/A'
  if (unitString === '%') {
    return (number * 100).toFixed(decimalPlaces) + unitString
  }
  return number.toFixed(decimalPlaces) + unitString
}

export default function TechnicianScorecardModal(
  props: ITechnicianScorecardModalProps,
) {
  const { isOpen, setIsOpen, technicianScorecard, setTechnicianScorecard } =
    props
  const [weights, setWeights] = useState<
    Record<keyof typeof SCORECARD_WEIGHTS, number>
  >(initialScorecardWeights)
  const [targets, setTargets] = useState<
    Record<keyof typeof SCORECARD_TARGETS, number>
  >(initialScorecardTargets)

  useEffect(() => {
    getAndSetScorecardWeightsAndTargets(setWeights, setTargets)
  }, [])

  if (!technicianScorecard) return <> </>

  const {
    driverSafety,
    customerRating,
    installationAudit,
    fourTireInstallTime,
    mileageCapture,
    onTimeClockIn,
    qualityScore,
    overallRank,
    overallScore,
    technician,
    totalInstallations,
  } = technicianScorecard

  async function handleClosed(): Promise<void> {
    setIsOpen(false)
    setTechnicianScorecard(null)
  }

  const tenure = getElapsedTime(
    new Date(technician.employmentStartDate),
    new Date(),
  )
  const tenureString = technician.employmentStartDate
    ? `${tenure.years}-years, ${tenure.months}-months, ${tenure.days}-days`
    : 'N/A'

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => handleClosed()}
      >
        <Box className={styles.technicianScorecardModal}>
          <div className={styles.modalContainer}>
            <div className={styles.modalTitle}>
              <div style={{ fontWeight: 700 }}>{technician.name}</div>
            </div>
            <div className={styles.contentContaier}>
              <div className={styles.sectionContainer}>
                <div className={styles.entryContainer}>
                  {ENTRY.employeeId}
                  <span className={styles.dotted}></span>
                  <span>{technician.employeeId}</span>
                </div>
                <div className={styles.entryContainer}>
                  {ENTRY.tenure}
                  <span className={styles.dotted}></span>
                  <span>{tenureString}</span>
                </div>
              </div>
              {qualityScore && (
                <div className={styles.sectionContainer}>
                  {SECTION.qualityScore}
                  <div className={styles.entryContainer}>
                    {ENTRY.qualityScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        qualityScore.qualityScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.qualifyingDefects}
                    <span className={styles.dotted}></span>
                    <span>{qualityScore.numberOfDefects ?? 0}</span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.totalInstallations}
                    <span className={styles.dotted}></span>
                    <span>{totalInstallations ?? 0}</span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(weights.qualityScoreWeight, '%')}
                    </span>
                  </div>
                </div>
              )}
              {driverSafety && (
                <div className={styles.sectionContainer}>
                  {SECTION.driverSafety}
                  <div className={styles.entryContainer}>
                    {ENTRY.driverSafetyScore}{' '}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        driverSafety.driverSafetyScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.numberOfHarshEvents}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(driverSafety.harshEvents, '', 0)}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.milesDriven}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        driverSafety.milesDriven,
                        'mi',
                        2,
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.speedingTimeTotalTime}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        driverSafety.minutesOverSpeedLimit,
                        'min',
                        0,
                      )}{' '}
                      /{' '}
                      {formatNumberForDisplay(
                        driverSafety.minutesDriven,
                        'min',
                        0,
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(weights.driverSafetyWeight, '%')}
                    </span>
                  </div>
                </div>
              )}
              {customerRating && (
                <div className={styles.sectionContainer}>
                  {SECTION.customerRating}
                  <div className={styles.entryContainer}>
                    {ENTRY.customerRatingScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        customerRating.customerRatingScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.numberOfJobsRated}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        customerRating.numberOfJobsRatedByCustomers,
                        '',
                        0,
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {`% Jobs Rated by Customers (Target > ${
                      targets.percentageOfJobsRatedByCustomersTarget * 100
                    }%):`}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        customerRating.percentageOfJobsRatedByCustomers,
                        '%',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.averageCustomerRating}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        customerRating.averageCustomerRating,
                        '',
                        2,
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        weights.averageCustomerRatingWeight +
                          weights.percentageOfJobsRatedByCustomersWeight,
                        '%',
                      )}
                    </span>
                  </div>
                </div>
              )}
              {installationAudit && (
                <div className={styles.sectionContainer}>
                  {SECTION.installationAudits}
                  <div className={styles.entryContainer}>
                    {ENTRY.installationAuditScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        installationAudit.installationAuditScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.percentPassingInstallationAudits}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        installationAudit.installationAuditPassPercentage,
                        '%',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        weights.installationAuditPassPercentageWeight,
                        '%',
                      )}
                    </span>
                  </div>
                </div>
              )}
              {fourTireInstallTime && (
                <div className={styles.sectionContainer}>
                  {SECTION.fourTireInstallTime}
                  <div className={styles.entryContainer}>
                    {ENTRY.fourTireInstallationScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        fourTireInstallTime.fourTireInstallTimeScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {`Average 4-Tire Install Time (Target < ${targets.averageFourTireInstallTimeTarget}min):`}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        fourTireInstallTime.averageFourTireInstallTime,
                        'min',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        weights.averageFourTireInstallTimeWeight,
                        '%',
                      )}
                    </span>
                  </div>
                </div>
              )}
              {mileageCapture && (
                <div className={styles.sectionContainer}>
                  {SECTION.mileageCapture}
                  <div className={styles.entryContainer}>
                    {ENTRY.mileageCaptureScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        mileageCapture.mileageCaptureScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {`% Jobs with Mileage Capture (Target > ${
                      targets.mileageCapturePercentageTarget * 100
                    }%):`}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        mileageCapture.mileageCapturePercentage,
                        '%',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        weights.mileageCapturePercentageWeight,
                        '%',
                      )}
                    </span>
                  </div>
                </div>
              )}
              {onTimeClockIn && (
                <div className={styles.sectionContainer}>
                  {SECTION.onTimeClockIn}
                  <div className={styles.entryContainer}>
                    {ENTRY.onTimeClockInScore}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        onTimeClockIn.onTimeClockInScore,
                        '/100',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.percentOnTimeClockIns}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        onTimeClockIn.onTimeClockInPercentage,
                        '%',
                      )}
                    </span>
                  </div>
                  <div className={styles.entryContainer}>
                    {ENTRY.weight}
                    <span className={styles.dotted}></span>
                    <span>
                      {formatNumberForDisplay(
                        weights.onTimeClockInPercentageWeight,
                        '%',
                      )}
                    </span>
                  </div>
                </div>
              )}
              <div
                className={styles.sectionContainer}
                style={{ alignSelf: 'center', flexDirection: 'row' }}
              >
                <div className={styles.overallContainer}>
                  {ENTRY.overallScore}
                  <span>{formatNumberForDisplay(overallScore)}</span>
                  {' | '}
                  {ENTRY.overallRank}
                  <span>{overallRank}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <PrimaryButton
              buttonName={'Close'}
              onClick={handleClosed}
              style={{ width: '100px', marginTop: '10px' }}
            />
          </div>
        </Box>
      </Modal>
    </>
  )
}
